import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { UserPlus, KeyRound, Trash2, PowerOff, Power, Edit3, X } from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null); // {mode: 'create'|'reset', user?}
  const [form, setForm] = useState({ username: "", name: "", password: "", email: "", phone: "" });
  const [err, setErr] = useState("");

  const load = async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ username: "", name: "", password: "", email: "", phone: "", commission_rate: 0 }); setErr(""); setModal({ mode: "create" }); };
  const openReset = (u) => { setForm({ password: "" }); setErr(""); setModal({ mode: "reset", user: u }); };
  const close = () => setModal(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      if (modal.mode === "create") {
        await api.post("/users", form);
      } else if (modal.mode === "reset") {
        await api.post(`/users/${modal.user.id}/reset-password`, { new_password: form.password });
      }
      close();
      load();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Failed");
    }
  };

  const toggleDisable = async (u) => {
    await api.patch(`/users/${u.id}`, { disabled: !u.disabled });
    load();
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete connector "${u.username}"? This cannot be undone.`)) return;
    await api.delete(`/users/${u.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Connector Management</h1>
          <p className="text-sm text-muted-foreground">Create, disable and manage connector accounts</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate} data-testid="btn-add-connector"><UserPlus size={16} /> New Connector</button>
      </div>

      <div className="card overflow-hidden">
        <table className="table-clean">
          <thead>
            <tr><th>Username</th><th>Name</th><th>Email</th><th>Phone</th><th>Leads</th><th>Status</th><th>Created</th><th className="text-right">Actions</th></tr>
          </thead>
          <tbody data-testid="users-table-body">
            {users.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-8">No connectors yet. Create the first one above.</td></tr>}
            {users.map((u) => (
              <tr key={u.id}>
                <td className="font-mono font-semibold">{u.username}</td>
                <td>{u.name}</td>
                <td className="text-muted-foreground">{u.email || "—"}</td>
                <td className="tabular text-muted-foreground">{u.phone || "—"}</td>
                <td className="tabular font-semibold">{u.lead_count}</td>
                <td>{u.disabled ? <span className="badge badge-red">Disabled</span> : <span className="badge badge-green">Active</span>}</td>
                <td className="text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="text-right space-x-1">
                  <button className="btn btn-ghost !p-2" onClick={() => openReset(u)} title="Reset password" data-testid={`btn-reset-${u.username}`}><KeyRound size={16} /></button>
                  <button className="btn btn-ghost !p-2" onClick={() => toggleDisable(u)} title={u.disabled ? "Enable" : "Disable"} data-testid={`btn-toggle-${u.username}`}>
                    {u.disabled ? <Power size={16} /> : <PowerOff size={16} />}
                  </button>
                  <button className="btn btn-ghost !p-2 text-red-600" onClick={() => remove(u)} title="Delete" data-testid={`btn-delete-${u.username}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="card p-6 w-full max-w-md fade-in" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">{modal.mode === "create" ? "Create Connector" : `Reset password for ${modal.user.username}`}</h3>
              <button className="btn btn-ghost !p-1.5" onClick={close}><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              {modal.mode === "create" && (
                <>
                  <div><label className="text-xs font-semibold text-muted-foreground">Username *</label>
                    <input required className="input mt-1" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="modal-username" /></div>
                  <div><label className="text-xs font-semibold text-muted-foreground">Full Name *</label>
                    <input required className="input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="modal-name" /></div>
                  <div><label className="text-xs font-semibold text-muted-foreground">Email</label>
                    <input type="email" className="input mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="modal-email" /></div>
                  <div><label className="text-xs font-semibold text-muted-foreground">Phone</label>
                    <input className="input mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="modal-phone" /></div>
                  <div><label className="text-xs font-semibold text-muted-foreground">Commission Rate (%)</label>
                    <input type="number" step="0.1" min="0" className="input mt-1" value={form.commission_rate || 0} onChange={(e) => setForm({ ...form, commission_rate: parseFloat(e.target.value) || 0 })} data-testid="modal-commission" /></div>
                </>
              )}
              <div><label className="text-xs font-semibold text-muted-foreground">{modal.mode === "reset" ? "New Password *" : "Password *"}</label>
                <input required type="password" className="input mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="modal-password" /></div>
              {err && <div className="text-sm text-red-600" data-testid="modal-error">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-outline" onClick={close}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="modal-submit">{modal.mode === "create" ? "Create" : "Reset"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
